import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PAGE_PATH, SIDE_BAR_MODE, SIDE_BAR_MOUSE_EVENTS } from './constants/constant';
import Sidebar from './sidebar/Sidebar';
import styles from './Dashboard.styles';
import { componentKey, resetDashboardState, setMenuActive, setShowChangePlan, setShowPrepayWalletDrawer, setShowPaymentNotAllowedPopup, setIsTinUnmaskAllowed, setOnboardingPending } from "./DashboardSlice";
import TxNavbar from './navbar/TxNavbar';
import ComponentDirectory from '../componentdirectory/ComponentDirectory';
import { Navigate, Route, Routes, useLocation,useNavigate, useSearchParams } from 'react-router-dom';
import FormsSection from '../formssection/FormsSection';
import PayersSection from '../payerssection/PayersSection';
import SupportSection from '../supportsection/SupportSection';
import ReportsSection from '../reportssection/ReportsSection';
import RecipientsSection from '../recipientssection/RecipientsSection';
import UsersSection from '../users/UsersSection';
import NoticesSection from '../noticessection/NoticesSection';
import HomeSection from '../homesection/HomeSection';
import Onboarding from '../onboarding/Onboarding';
import { componentKey as authenticationComponentKey, setIsUserAuthenticated, setShowSessionError } from '../authentication/AuthenticationSlice';
import { getUserProfileAndPermissions } from './DashboardSaga';
import Loadable from '../../components/loadable/Loadable';
import { ONBOARDING_ROUTES } from '../onboarding/constants';
import handleResponse from '../../../libs/responsemiddleware/HandleResponse';
import { addNotifications } from '../toaster/ToasterPortalSlice';
import { USER_TYPE } from '../../../libs/customhooks/constants';
import { useUserTypeInfo } from '../../../libs/customhooks/useUserTypeInfo';
import ProfileDrawer from './profiledrawer/ProfileDrawer';
import AccountSection from '../accountsection/AccountSection';
import General from '../../../libs/utility/General';
import HistorySection from '../history/HistorySection';
import ApiSection from '../apisection/ApiSection';
import PaymentDrawer from '../components/paymentdrawer/PaymentDrawer';
import { PAYMENT_STEPS } from '../components/paymentdrawer/constants';
import FileShare from '../shortcuts/fileshare/FileShare';
import CheckStatus from '../shortcuts/checkstatus/CheckStatus';
import GenerateSummary from '../shortcuts/generatesummary/GenerateSummary';
import ManageExtensionForm from '../shortcuts/manageextensionform/ManageExtensionForm';
import { PAGE_STATE, INTEGRATION_ROUTES, PRE_LOGIN_ROUTES } from '../constants/constants';
import useGarbageCollector from '../../../libs/garbagecollector/garbageCollectorHooks';
import ImportSection from '../importsection/ImportSection';
import InformationPopup from '../../components/popup/informationpopup/InformationPopup';
import { VEC_ICON_NAME } from '../../components/icons/constants';
import { BUTTON_TYPE } from '../../components/button/constants';
import { COOKIES_SETTING_PATHS, DASHBOARD_STATES, DASHBOARD_STATES_MESSAGE_MAP } from './constants';
import useTxNavigate from '../../../libs/txnavigate/TxNavigateHook';
import { PERFTEST_EMAIL_DOMAIN } from '../../../libs/utility/constants';
import "./directcss/Dashboard.scss"
import ManageCards from './managecards/ManageCards';
import ChangePlan from '../components/changeplan/ChangePlan';
import BoiReportingSection from '../boireportingsection/BoiReportingSection';
import { INTEGRATION_CONSTANTS } from '../importsection/integrations/constants';
import OtherAppsDrawer from './otherappsdrawer/OtherAppsDrawer';
import { REDIRECT_TO } from '../preloginscreens/constants';

export default function Dashboard() {

	useGarbageCollector(componentKey);

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const navigateTx = useTxNavigate();
	const location = useLocation();
	const [queryParams] = useSearchParams();
	const timeoutRef = useRef(null);

	handleResponse((url) => navigateTx(url), (errorMsgObj) => dispatch(addNotifications(errorMsgObj)), (status) => dispatch(setIsUserAuthenticated(status)), () => dispatch(setShowSessionError(true)));

	const { isMenuActive, sideBarMode, loadingState, onboardingPending, onBoardingScreenToStart, showProfileDrawer, showOtherAppsDrawer, showChangePlan, showPrepayWalletDrawer, showPaymentNotAllowedPopup, showManageCardDrawer, errorInProfile, toggled } = useSelector((state) => state[componentKey])

	const { isUserAuthenticated, authenticationLoader, signedInUser } = useSelector((state) => state[authenticationComponentKey])
	const { userType } = useUserTypeInfo();

	useEffect(() => {

		if (queryParams.has(INTEGRATION_CONSTANTS.INTEGRATION_PARAM)) {
			const integrationValue = queryParams.get(INTEGRATION_CONSTANTS.INTEGRATION_PARAM);
			const otherParams = new URLSearchParams();
			queryParams.forEach((value, key) => {
				if (key !== INTEGRATION_CONSTANTS.INTEGRATION_PARAM) {
					otherParams.append(key, value);
				}
			});
			if (integrationValue === INTEGRATION_CONSTANTS.QBD_TEXT) {
				const source = queryParams.get(INTEGRATION_CONSTANTS.SOURCE_TEXT);
				otherParams.delete(INTEGRATION_CONSTANTS.SOURCE_TEXT);
				const newPath = `${INTEGRATION_ROUTES.IMPORT}/${integrationValue}/${source}`;
				navigate(`${newPath}?${otherParams}`)
			}
			else if (integrationValue) {
				const newPath = `${INTEGRATION_ROUTES.IMPORT}/${integrationValue}`;
				navigate(`${newPath}?${otherParams}`)
			}
		}
	}, []);

	useEffect(() => {
		window.gtag("event", "page_view", {
			page_path: location.pathname + location.search + location.hash,
			page_search: location.search,
			page_hash: location.hash,
		});
	}, [location]);

	useEffect(() => {
		const updateCookieBadgeVisibility = () => {
			const cookieBadge = document.getElementById('cookiescript_badge');
			if (!cookieBadge) {
			  return;
			}
	  
			const windowPath = location.pathname;
			const isCookieSettingPath = COOKIES_SETTING_PATHS.some(path => windowPath.startsWith(path));
	  
			cookieBadge.style.display = isCookieSettingPath ? '' : 'none';
		  };
	  
		  updateCookieBadgeVisibility();
	  
		  const observer = new MutationObserver(() => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
      		timeoutRef.current = setTimeout(updateCookieBadgeVisibility, 100);
		  });
	  
		  if (!document.getElementById('cookiescript_badge')) {
			observer.observe(document.body, { childList: true, subtree: true });
		  }
	  
		  return () => {
			observer.disconnect();
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		  };
	}, [location])

	useEffect(() => {
		if (!isUserAuthenticated && authenticationLoader.state === PAGE_STATE.PAGE_READY) {
			dispatch(resetDashboardState());
			const redirectUrl = General.getFinalRouteToSignInPage();
			let appendedRoute = "";
			if(redirectUrl !== General.encodeString("/")){
				appendedRoute = `?${REDIRECT_TO}=${General.getFinalRouteToSignInPage()}`;
			}
			navigateTx(`/${PRE_LOGIN_ROUTES.SIGNIN}${appendedRoute}`);
		} else if (authenticationLoader.state === PAGE_STATE.PAGE_READY) {
			dispatch(getUserProfileAndPermissions());

		}
	}, [isUserAuthenticated, authenticationLoader]);

	useEffect(() => {
		dispatch(setIsTinUnmaskAllowed(signedInUser?.IsTinUnmaskAllowedEnableAtUserLevel))
	}, [signedInUser])

	useEffect(() => {
		if (!signedInUser?.EncUID) {
			return
		}

		const id = signedInUser?.EncUID

		const userDomain = General.getUserDomain(signedInUser.Email)

		if (userDomain !== PERFTEST_EMAIL_DOMAIN) {
			window.userGuiding.identify(id);
		}

	}, [signedInUser?.EncUID])

	const mouseActionHandler = (event) => {
		if (sideBarMode == SIDE_BAR_MODE.HOVER) {
			if (event?.type == SIDE_BAR_MOUSE_EVENTS.MOUSE_ENTER) {
				dispatch(setMenuActive(true));
			} else if (event?.type == SIDE_BAR_MOUSE_EVENTS.MOUSE_LEAVE) {
				dispatch(setMenuActive(false));
			}
		}
	}

	useEffect(() => {
		if(errorInProfile){
			dispatch(setOnboardingPending(false))
		}
	}, [errorInProfile])

	return (
		<>
			<Loadable currentState={loadingState.state} loadingStates={[DASHBOARD_STATES.PAGE_LOADING]} messageMap={DASHBOARD_STATES_MESSAGE_MAP} isNewLoadable={true}>
				<div style={styles.mainContainer}>
					<TxNavbar />
					<div style={styles.secondLevelContainer}>
						{
							(!errorInProfile) && <>
								{
									(onboardingPending && General.hasAccessToUserType(userType, [USER_TYPE.ESSENTIAL_USER, USER_TYPE.EFILE_PLUS_USER, USER_TYPE.ENTERPRISE_USER])) ?
									<div style={styles.dashboardContainer}>
										<div style={styles.dashboardBody}>
											<Routes>
												<Route path={`/${ONBOARDING_ROUTES.ONBOARDING}/*`} element={<Onboarding />} />
												<Route path={`${PAGE_PATH.DASHBOARD}*`} element={<Navigate to={`/${ONBOARDING_ROUTES.ONBOARDING}/${onBoardingScreenToStart}`} replace={true} />} />
											</Routes>
										</div>
									</div>
									:
									<>
										<Sidebar isMenuActive={isMenuActive} onMouseAction={mouseActionHandler} signedInUser={signedInUser} toggled={toggled} />
										<div style={styles.dashboardContainer}>
											<div style={styles.dashboardBody}>
												<Routes>
													<Route path="/forms/*" element={<FormsSection />} />
													<Route path="/notices/*" element={<NoticesSection />} />
													<Route path="/payer/*" element={<PayersSection />} />
													<Route path="/import/*" element={<ImportSection />} />
													<Route path="/recipient/*" element={<RecipientsSection />} />
													<Route path="/reports/*" element={<ReportsSection />} />
													<Route path="/support/*" element={<SupportSection />} />
													<Route path="/users/*" element={<UsersSection />} />
													<Route path="/history/*" element={<HistorySection />} />
													<Route path="/account/*" element={<AccountSection />} />
													<Route path="/api/*" element={<ApiSection />} />
													<Route path="/boireporting/*" element={<BoiReportingSection />} />
													<Route path="/fileshare/*" element={<FileShare />} />
													<Route path="/checkstatus/*" element={<CheckStatus />} />
													<Route path="/generatesummary/*" element={<GenerateSummary />} />
													<Route path="/manageextensionform/*" element={<ManageExtensionForm />} />
													<Route path="/componentdirectory/*" element={<ComponentDirectory />} />
													<Route exact path={PAGE_PATH.DASHBOARD} element={<HomeSection />} />
													<Route path={'/*'} element={<Navigate to={`/`} replace={true} />} />
												</Routes>
											</div>
										</div>
									</>
								}
							</>
						}
					</div>
				</div>
				{showProfileDrawer && <ProfileDrawer />}
				{showOtherAppsDrawer && <OtherAppsDrawer />}
				{showManageCardDrawer && <ManageCards />}
				{showChangePlan && <ChangePlan showPopup={showChangePlan} closePopup={() => { dispatch(setShowChangePlan(false)) }} />}

				{showPrepayWalletDrawer &&
					<PaymentDrawer
						isOpen={showPrepayWalletDrawer}
						onCloseCb={() => dispatch(setShowPrepayWalletDrawer(false))}
						defaultStep={PAYMENT_STEPS.ADD_FUNDS}
					/>
				}

				{showPaymentNotAllowedPopup && <InformationPopup
					title={"Please Note"}
					bodyText={"You do not have the access to payments. Please contact the Admin."}
					iconName={VEC_ICON_NAME.WARNING_ICON}
					isOpen={showPaymentNotAllowedPopup}
					confirmationButtonProps={{
						onConfirmationCb: () => dispatch(setShowPaymentNotAllowedPopup(false)),
						confirmationButtonText: "Ok",
						confirmationButtonStyles: {},
						confirmationButtonType: BUTTON_TYPE.PRIMARY,
					}}
					showClose={false}
				/>}

				{errorInProfile && <InformationPopup
					title={"Error"}
					bodyText={"There was some error in fetching your profile."}
					iconName={VEC_ICON_NAME.WARNING_ICON}
					isOpen={errorInProfile}
					confirmationButtonProps={{
						onConfirmationCb: () => {window.location.reload()},
						confirmationButtonText: "Reload Site",
						confirmationButtonStyles: {},
						confirmationButtonType: BUTTON_TYPE.PRIMARY,
					}}
					showClose={false}
				/>}
			</Loadable>
		</>
	)
}